import React, { useEffect, useMemo, useState } from 'react';
import { Select, Checkbox, Modal } from 'antd';
import { fetchRegions, fetchDistricts, fetchHolidayLocations } from '../HolidaySchedule/saga';

const { Option } = Select;

interface Props {
  visible: boolean;
  initialSelected?: any[];
  allLocationsFromData?: { label: string; value: any }[];
  allRequestorData?: any[];
  onOk: (vals: any[], locationObjects?: { value: any; label: string }[]) => void;
  onCancel: () => void;
  positions?: string;
  selectedRequestorId?: number | null;
  showAllLocations?: boolean;
}

export default function LocationPickerPopup({
  visible,
  initialSelected = [],
  allLocationsFromData = [],
  allRequestorData = [],
  onOk,
  onCancel,
  positions = '',
  selectedRequestorId = null,
  showAllLocations = false,
}: Props) {
  const [regions, setRegions] = useState<{ id: number; name: string }[]>([]);
  const [districtOptions, setDistrictOptions] = useState<any[]>([]);
  const [locationOptions, setLocationOptions] = useState<any[]>([]);

  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<(string | number)[]>([]);

  const [selectAllRegions, setSelectAllRegions] = useState<boolean>(false);
  const [selectAllDistricts, setSelectAllDistricts] = useState<boolean>(false);
  const [selectAllLocations, setSelectAllLocations] = useState<boolean>(false);

  const locationMetaMap = useMemo(() => {
    const map: Record<string, { regionId?: number; districtId?: number }> = {};
    if (!allRequestorData) return map;
    allRequestorData.forEach((item: any) => {
      // Use same ID priority: id > Id > locationId > LocationSid
      const val = item.id ?? item.Id ?? item.locationId ?? item.LocationSid ?? item.LocationId ?? item.locationSid;
      if (!val) return;
      map[String(val)] = {
        regionId: item.RegionSid || item.RegionId || item.regionId || undefined,
        districtId: item.DistrictSid || item.DistrictId || item.districtId || undefined,
      };
    });
    return map;
  }, [allRequestorData]);

  // Extract allowed region IDs from allRequestorData for the selected requestor
  // This is used to filter regions when RVP/Admin selects a different requestor
  // Returns null when showAllLocations is true (for AV, RM, MOD Admin)
  const allowedRegionIds = useMemo(() => {
    if (showAllLocations) return null;
    if (!selectedRequestorId || !allRequestorData || allRequestorData.length === 0) return null;
    const regionIds = new Set<number>();
    allRequestorData
      .filter((item: any) => item.ContactSid === selectedRequestorId)
      .forEach((item: any) => {
        const regionId = item.RegionSid || item.RegionId || item.regionId;
        if (regionId) regionIds.add(regionId);
      });
    return regionIds.size > 0 ? regionIds : null;
  }, [selectedRequestorId, allRequestorData, showAllLocations]);

  useEffect(() => {
    if (!visible) return;

    // For DM/AM case (showAllLocations is false): extract regions from allRequestorData
    if (!showAllLocations && allRequestorData && allRequestorData.length > 0) {
      const regionMap = new Map<number, string>();
      allRequestorData
        .filter((item: any) => !selectedRequestorId || item.ContactSid === selectedRequestorId)
        .forEach((item: any) => {
          const regionId = item.RegionSid || item.RegionId || item.regionId;
          const regionName = item.RegionName || item.regionName;
          if (regionId && regionName && !regionMap.has(regionId)) {
            regionMap.set(regionId, regionName);
          }
        });
      const extractedRegions = Array.from(regionMap.entries()).map(([id, name]) => ({ id, name }));
      setRegions(extractedRegions);
      return;
    }

    // For other cases (AV, RM, MOD Admin): fetch regions from API
    let mounted = true;
    fetchRegions(positions)
      .then((res: any) => {
        if (!mounted) return;
        if (Array.isArray(res)) {
          let fetchedRegions = res.map((r: any) => ({ id: r.id, name: r.name }));
          // Filter regions by selected requestor's allowed regions if applicable
          if (allowedRegionIds) {
            fetchedRegions = fetchedRegions.filter((r) => allowedRegionIds.has(r.id));
          }
          setRegions(fetchedRegions);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [visible, allowedRegionIds, showAllLocations, allRequestorData, selectedRequestorId, positions]);

  // Extract allowed district IDs from allRequestorData for the selected requestor
  // Returns null when showAllLocations is true (for AV, RM, MOD Admin)
  const allowedDistrictIds = useMemo(() => {
    if (showAllLocations) return null;
    if (!selectedRequestorId || !allRequestorData || allRequestorData.length === 0) return null;
    const districtIds = new Set<number>();
    allRequestorData
      .filter((item: any) => item.ContactSid === selectedRequestorId)
      .forEach((item: any) => {
        const districtId = item.DistrictSid || item.DistrictId || item.districtId;
        if (districtId) districtIds.add(districtId);
      });
    return districtIds.size > 0 ? districtIds : null;
  }, [selectedRequestorId, allRequestorData, showAllLocations]);

  useEffect(() => {
    if (!selectedRegions || selectedRegions.length === 0) {
      setDistrictOptions([]);
      return;
    }
    let mounted = true;
    Promise.all(selectedRegions.map((id) => fetchDistricts(id, positions)))
      .then((results: any[]) => {
        if (!mounted) return;
        const combined = results.flat();
        let opts = (combined || [])
          .map((d: any) => ({ label: d.name || d.Name || '', value: String(d.id), regionId: d.regionId || d.RegionId }))
          .filter((o: any) => o.label);
        // Filter districts by selected requestor's allowed districts if applicable
        if (allowedDistrictIds) {
          opts = opts.filter((d) => allowedDistrictIds.has(Number(d.value)));
        }
        const unique = Array.from(new Map(opts.map((o: any) => [o.value, o])).values());
        setDistrictOptions(unique);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [selectedRegions, allowedDistrictIds]);

  // Extract allowed location IDs from allRequestorData for the selected requestor
  // Returns null when showAllLocations is true (for AV, RM, MOD Admin)
  const allowedLocationIds = useMemo(() => {
    if (showAllLocations) return null;
    if (!selectedRequestorId || !allRequestorData || allRequestorData.length === 0) return null;
    const locationIds = new Set<number>();
    allRequestorData
      .filter((item: any) => item.ContactSid === selectedRequestorId)
      .forEach((item: any) => {
        // Use same ID priority: id > Id > locationId > LocationSid
        const locationId =
          item.id ?? item.Id ?? item.locationId ?? item.LocationSid ?? item.LocationId ?? item.locationSid;
        if (locationId) locationIds.add(Number(locationId));
      });
    return locationIds.size > 0 ? locationIds : null;
  }, [selectedRequestorId, allRequestorData, showAllLocations]);

  useEffect(() => {
    const regionMap = new Map<string, string[]>();
    selectedRegions.forEach((rid) => regionMap.set(rid, []));
    selectedDistricts.forEach((did) => {
      const found = districtOptions.find((d) => d.value === did);
      if (found && found.regionId != null) {
        const rid = String(found.regionId);
        if (!regionMap.has(rid)) regionMap.set(rid, []);
        regionMap.get(rid)!.push(did);
      }
    });

    const requests = Array.from(regionMap.entries())
      .filter(([, dids]) => dids && dids.length > 0)
      .map(([rid, dids]) => fetchHolidayLocations({ RegionId: Number(rid), DistrictIds: dids.map(Number) }, positions));

    if (requests.length === 0) {
      setLocationOptions([]);
      return;
    }

    let mounted = true;
    Promise.all(requests)
      .then((results: any[]) => {
        if (!mounted) return;
        const combined = results.flat();
        let opts = (combined || [])
          .map((l: any) => {
            const locationName = l.LocationName || l.Location || l.name || l.Name || '';
            // Use the same ID field priority as the dropdown: id > Id > locationId > LocationSid
            const locationId = l.id ?? l.Id ?? l.locationId ?? l.LocationSid;
            if (!locationId) return null; // Skip if no valid ID
            const label =
              (l.LocationNumber || l.number || l.Number || '') +
              ' - ' +
              (locationName || l.code || l.Code || String(locationId));
            return { label, value: locationId };
          })
          .filter((item): item is { label: string; value: any } => item !== null);
        // Filter locations by selected requestor's allowed locations if applicable
        if (allowedLocationIds) {
          opts = opts.filter((l) => allowedLocationIds.has(Number(l.value)));
        }
        const unique = Array.from(new Map(opts.map((o: any) => [o.value, o])).values());
        setLocationOptions(unique);
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, [selectedRegions, selectedDistricts, districtOptions, allowedLocationIds]);

  // removed auto-clear effects to avoid clearing all selections when user deselects a single item

  // keep select-all checkboxes in sync with manual selections
  useEffect(() => {
    setSelectAllRegions(regions.length > 0 && selectedRegions.length === regions.length);
  }, [regions, selectedRegions]);

  useEffect(() => {
    setSelectAllDistricts(districtOptions.length > 0 && selectedDistricts.length === districtOptions.length);
  }, [districtOptions, selectedDistricts]);

  useEffect(() => {
    setSelectAllLocations(locationOptions.length > 0 && selectedLocations.length === locationOptions.length);
  }, [locationOptions, selectedLocations]);

  useEffect(() => {
    // initialize selections from initialSelected using locationMetaMap
    if (!visible) return;
    // Convert to numbers to match dropdown format
    setSelectedLocations((initialSelected || []).map((v: any) => (typeof v === 'number' ? v : Number(v) || v)));
    const regs = new Set<string>();
    const dists = new Set<string>();
    (initialSelected || []).forEach((val: any) => {
      const m = locationMetaMap[String(val)];
      if (m?.regionId) regs.add(String(m.regionId));
      if (m?.districtId) dists.add(String(m.districtId));
    });
    setSelectedRegions(Array.from(regs));
    setSelectedDistricts(Array.from(dists));
  }, [visible, initialSelected, locationMetaMap]);

  // Build the final selected location objects with labels for the parent
  const getSelectedLocationObjects = () => {
    const locationMap = new Map(locationOptions.map((o) => [o.value, o.label]));
    return selectedLocations.map((val) => ({
      value: val,
      label: locationMap.get(val) || String(val),
    }));
  };

  return (
    <Modal
      title='Select Locations'
      open={visible}
      onOk={() => onOk(selectedLocations, getSelectedLocationObjects())}
      onCancel={onCancel}
      width={720}
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200, flex: '1 1 200px' }}>
          <Checkbox
            checked={selectAllRegions}
            onChange={(e) => {
              const checked = Boolean(e.target.checked);
              setSelectAllRegions(checked);
              if (checked) setSelectedRegions(regions.map((r) => String(r.id)));
              else setSelectedRegions([]);
              // Clear districts and locations when regions change
              setSelectAllDistricts(false);
              setSelectedDistricts([]);
              setSelectAllLocations(false);
              setSelectedLocations([]);
            }}
          >
            Select All Regions
          </Checkbox>
          <Select
            mode='multiple'
            allowClear
            value={selectedRegions}
            onChange={(vals) => {
              setSelectedRegions((vals as string[]) || []);
              // Clear districts and locations when regions change
              setSelectAllDistricts(false);
              setSelectedDistricts([]);
              setSelectAllLocations(false);
              setSelectedLocations([]);
            }}
            style={{ width: '100%', marginTop: 8 }}
            options={regions.map((r) => ({ label: r.name, value: String(r.id) }))}
            placeholder='Select regions'
            maxTagCount={1}
            maxTagPlaceholder={(omitted) => `+${omitted.length}`}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        </div>

        <div style={{ minWidth: 200, flex: '1 1 200px' }}>
          <Checkbox
            checked={selectAllDistricts}
            onChange={(e) => {
              const checked = Boolean(e.target.checked);
              setSelectAllDistricts(checked);
              if (checked) setSelectedDistricts(districtOptions.map((d) => d.value));
              else setSelectedDistricts([]);
              // Clear locations when districts change
              setSelectAllLocations(false);
              setSelectedLocations([]);
            }}
          >
            Select All Districts
          </Checkbox>
          <Select
            mode='multiple'
            allowClear
            value={selectedDistricts}
            onChange={(vals) => {
              setSelectedDistricts((vals as string[]) || []);
              // Clear locations when districts change
              setSelectAllLocations(false);
              setSelectedLocations([]);
            }}
            style={{ width: '100%', marginTop: 8 }}
            options={districtOptions}
            placeholder='Select districts'
            maxTagCount={1}
            maxTagPlaceholder={(omitted) => `+${omitted.length}`}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        </div>

        <div style={{ minWidth: 240, flex: '1 1 240px' }}>
          <Checkbox
            checked={selectAllLocations}
            onChange={(e) => {
              const checked = Boolean(e.target.checked);
              setSelectAllLocations(checked);
              if (checked) setSelectedLocations(locationOptions.map((o) => o.value));
              else setSelectedLocations([]);
            }}
          >
            Select All Locations
          </Checkbox>
          <Select
            mode='multiple'
            allowClear
            value={selectedLocations}
            onChange={(vals) => setSelectedLocations((vals as (string | number)[]) || [])}
            style={{ width: '100%', marginTop: 8 }}
            options={(locationOptions || []).map((o) => ({ label: o.label, value: o.value }))}
            placeholder='Select locations'
            maxTagCount={1}
            maxTagPlaceholder={(omitted) => `+${omitted.length}`}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        </div>
      </div>

      {/* removed fallback all-locations selector per UX request */}
    </Modal>
  );
}
